/*
A MessagePack serializer.
See also: https://msgpack.org/
Based on: https://github.com/nailgilaziev/messagepack
Based on: https://github.com/ygoe/msgpack.js
*/

/*
Copyright 2020 Nail Gilaziev

Redistribution and use in source and binary forms, with or without modification,
are permitted provided that the following conditions are met:

1. Redistributions of source code must retain the above copyright notice,
   this list of conditions and the following disclaimer.

2. Redistributions in binary form must reproduce the above copyright notice,
  this list of conditions and the following disclaimer in the documentation and/or
  other materials provided with the distribution.

3. Neither the name of the copyright holder nor the names of its contributors may be used to
endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES,
INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

/*
Copyright © 2019, Yves Goergen, https://unclassified.software/source/msgpack-js

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and
associated documentation files (the “Software”), to deal in the Software without restriction,
including without limitation the rights to use, copy, modify, merge, publish, distribute,
sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or
substantial portions of the Software.

THE SOFTWARE IS PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT
NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import { BinaryReader } from './binary-reader.js';

export class BinaryUnpacker {
  private readonly reader: BinaryReader;

  constructor(reader: BinaryReader) {
    this.reader = reader;
  }

  static fromBytes(bytes: Uint8Array): BinaryUnpacker {
    return new BinaryUnpacker(BinaryReader.fromBytes(bytes));
  }

  unpackByte(): number {
    return this.reader.readByte();
  }

  unpackByteOrNull(): number | null {
    const b = this.reader.readByte();
    return b == 0xc0 ? null : b;
  }

  unpackBool(): boolean {
    const v = this.unpackBoolOrNull();
    if(v == null) {
      throw new Error('Unexpected null value');
    }

    return v;
  }

  unpackBoolOrNull(): boolean | null {
    const b = this.reader.readByte();
    let v: boolean | null = null;

    if(b == 0xc0) {
      v = null;
    }
    else if(b == 0xc2) {
      v = false;
    }
    else if(b == 0xc3) {
      v = true;
    }
    else {
      throw this.formatException('bool', b);
    }

    return v;
  }

  unpackInt(): number {
    const v = this.unpackIntOrNull();
    if(v == null) {
      throw new Error('Unexpected null value');
    }

    return v;
  }

  unpackIntOrNull(): number | null {
    const b = this.reader.readByte();
    let v: number | null = null;

    if(b == 0xc0) {
      v = null;
    }
    else if(b == 0xcc) {
      v = this.reader.readUint8();
    }
    else if(b == 0xcd) {
      v = this.reader.readUint16();
    }
    else if(b == 0xce) {
      v = this.reader.readUint32();
    }
    else if(b == 0xcf) {
      // v = this.reader.readUint64();
      throw new Error('Uint64 is not supported by BinaryUnpacker');
    }
    else if(b == 0xd0) {
      v = this.reader.readInt8();
    }
    else if(b == 0xd1) {
      v = this.reader.readInt16();
    }
    else if(b == 0xd2) {
      v = this.reader.readInt32();
    }
    else if(b == 0xd3) {
      // v = this.reader.readInt64();
      throw new Error('Int64 is not supported by BinaryUnpacker');
    }
    else {
      throw this.formatException('integer', b);
    }

    return v;
  }

  unpackDouble(): number {
    const v = this.unpackDoubleOrNull();
    if(v == null) {
      throw new Error('Unexpected null value');
    }

    return v;
  }

  unpackDoubleOrNull(): number | null {
    const b = this.reader.readByte();
    let v: number | null = null;

    if(b == 0xc0) {
      v = null;
    }
    else if(b == 0xca) {
      v = this.reader.readFloat32();
    }
    else if(b == 0xcb) {
      v = this.reader.readFloat64();
    }
    else {
      throw this.formatException('double', b);
    }

    return v;
  }

  unpackString(): string {
    const v = this.unpackStringOrNull();
    if(v == null) {
      throw new Error('Unexpected null value');
    }

    return v;
  }

  unpackStringOrNull(): string | null {
    const b = this.reader.peekByte();
    if(b == 0xc0) {
      this.reader.readByte();
      return null;
    }

    const len = this.unpackLength();
    const data = this.reader.readBytes(len);

    return decodeUtf8(data);
  }

  unpackBinaryBlob(): Uint8Array {
    const v = this.unpackBinaryBlobOrNull();
    if(v == null) {
      throw new Error('Unexpected null value');
    }

    return v;
  }

  unpackBinaryBlobOrNull(): Uint8Array | null {
    const b = this.reader.peekByte();
    if(b == 0xc0) {
      this.reader.readByte();
      return null;
    }

    const len = this.unpackLength();
    return this.reader.readBytes(len);
  }

  unpackLength(): number {
    const b = this.reader.readByte();
    let len: number | null = null;

    if(b == 0xcc) {
      len = this.reader.readUint8();
    }
    else if(b == 0xcd) {
      len = this.reader.readUint16();
    }
    else if(b == 0xce) {
      len = this.reader.readUint32();
    }
    else {
      throw this.formatException('length', b);
    }

    return len;
  }

  private formatException(type: string, b: number) {
    return new Error(`Try to unpack ${type} value, but it's not an ${type}, byte = 0x${b.toString(16)}`);
  }
}

// Decodes a string from UTF-8 bytes.
function decodeUtf8(bytes: Uint8Array) {
  // Based on: https://gist.github.com/pascaldekloe/62546103a1576803dade9269ccf76330
  let i = 0;
  let str = '';
  const length = bytes.byteLength;

  while(i < length) {
    let c = bytes[i++];
    if(c > 127) {
      if(c > 191 && c < 224) {
        if(i >= length) {
          throw new Error('UTF-8 decode: incomplete 2-byte sequence');
        }

        c = (c & 31) << 6 | bytes[i++] & 63;
      }
      else if(c > 223 && c < 240) {
        if(i + 1 >= length) {
          throw new Error('UTF-8 decode: incomplete 3-byte sequence');
        }

        c = (c & 15) << 12 | (bytes[i++] & 63) << 6 | bytes[i++] & 63;
      }
      else if(c > 239 && c < 248) {
        if(i + 2 >= length) {
          throw new Error('UTF-8 decode: incomplete 4-byte sequence');
        }

        c = (c & 7) << 18 | (bytes[i++] & 63) << 12 | (bytes[i++] & 63) << 6 | bytes[i++] & 63;
      }
      else throw new Error('UTF-8 decode: unknown multibyte start 0x' + c.toString(16) + ' at index ' + (i - 1));
    }
    if(c <= 0xffff) str += String.fromCharCode(c);
    else if(c <= 0x10ffff) {
      c -= 0x10000;
      str += String.fromCharCode(c >> 10 | 0xd800);
      str += String.fromCharCode(c & 0x3FF | 0xdc00);
    }
    else throw new Error('UTF-8 decode: code point 0x' + c.toString(16) + ' exceeds UTF-16 reach');
  }
  return str;
}
